<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import CloseCircleMemberFormSheet from './CloseCircleMemberFormSheet.vue';
import { useCloseCircleState } from './useCloseCircleState.js';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      beneficiaryType: 'main_contact',
      beneficiaryId: null,
      beneficiarySnapshot: null,
    }),
  },
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['update:modelValue', 'memberCreated']);

const { t } = useI18n();
const closeCircle = useCloseCircleState();
const formSheetRef = ref(null);

const members = computed(() => closeCircle.membersForContact(props.contactId));
const isLoading = computed(() => closeCircle.isLoading(props.contactId));
const error = computed(() => closeCircle.errorForContact(props.contactId));

const loadMembers = () => closeCircle.loadMembers(props.contactId);

onMounted(loadMembers);
watch(() => props.contactId, loadMembers);

const selectMainContact = () => {
  emit('update:modelValue', {
    beneficiaryType: 'main_contact',
    beneficiaryId: null,
    beneficiarySnapshot: null,
  });
};

const selectMember = member => {
  emit('update:modelValue', {
    beneficiaryType: 'close_circle_member',
    beneficiaryId: member.id,
    beneficiarySnapshot: closeCircle.beneficiarySnapshot(member),
  });
};

const openCreateMember = () => {
  formSheetRef.value?.open();
};

const activateCloseCircleMemberMode = () => {
  if (members.value[0]) {
    selectMember(members.value[0]);
  } else if (!isLoading.value) {
    openCreateMember();
  }
};

const saveMember = async ({ values, onSuccess, onError }) => {
  try {
    const member = await closeCircle.createMember(props.contactId, values);
    emit('memberCreated', member);
    selectMember(member);
    onSuccess?.();
  } catch (saveError) {
    onError?.(saveError);
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex rounded-lg bg-n-alpha-1 p-1">
      <button
        type="button"
        class="flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors"
        :class="
          modelValue.beneficiaryType === 'main_contact'
            ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
            : 'text-n-slate-10'
        "
        @click="selectMainContact"
      >
        {{ t('CLOSE_CIRCLE.BENEFICIARY.MAIN_CONTACT') }}
      </button>
      <button
        type="button"
        class="flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors"
        :class="
          modelValue.beneficiaryType === 'close_circle_member'
            ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
            : 'text-n-slate-10'
        "
        @click="activateCloseCircleMemberMode"
      >
        {{ t('CLOSE_CIRCLE.BENEFICIARY.CLOSE_CIRCLE_MEMBER') }}
      </button>
    </div>

    <div
      v-if="modelValue.beneficiaryType === 'close_circle_member'"
      class="flex flex-col gap-2"
    >
      <p v-if="isLoading && !members.length" class="text-sm text-n-slate-11">
        {{ t('CLOSE_CIRCLE.LOADING') }}
      </p>
      <p v-else-if="error && !members.length" class="text-sm text-n-ruby-11">
        {{ t('CLOSE_CIRCLE.LOAD_ERROR') }}
      </p>
      <button
        v-for="member in members"
        :key="member.id"
        type="button"
        class="flex items-center justify-between rounded-lg bg-n-alpha-1 px-3 py-2 text-left transition-colors hover:bg-n-alpha-2"
        :class="
          String(modelValue.beneficiaryId) === String(member.id)
            ? 'outline outline-1 outline-n-brand'
            : ''
        "
        @click="selectMember(member)"
      >
        <span class="min-w-0">
          <span class="block truncate text-sm font-medium text-n-slate-12">
            {{ closeCircle.memberName(member) }}
          </span>
          <span class="block truncate text-xs text-n-slate-10">
            {{
              t(closeCircle.relationshipConfig(member.relationship).labelKey)
            }}
          </span>
        </span>
        <span
          v-if="String(modelValue.beneficiaryId) === String(member.id)"
          class="i-lucide-check size-4 shrink-0 text-n-blue-11"
        />
      </button>
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-plus"
        :label="t('CLOSE_CIRCLE.BENEFICIARY.ADD_MEMBER')"
        @click="openCreateMember"
      />
    </div>

    <CloseCircleMemberFormSheet ref="formSheetRef" @save="saveMember" />
  </div>
</template>
